/**
 * A subject's documents and the upload flow: create the row, POST the bytes straight to S3,
 * and keep every moving row fresh — through the finaliser, detection and the ingest — until
 * it settles (useTransientPolling), whether it started here or before a reload. Below the
 * documents: the exam format learned from a past paper, New paper — a checklist of what a paper
 * waits for until a book is ready and the format confirmed, then the form — and the papers,
 * polled the same way while they are written. A paper asked for here lands in view.
 */
import { useCallback, useEffect, useRef, useState, type DragEvent } from "react";
import type {
  Api,
  ChapterWire,
  Doc,
  Meter,
  PaperRow,
  PastPaperListing,
  PastPaperRole,
  Profile,
  Subject,
} from "../api/api";
import { PastPapers, type UploadFailure } from "./PastPapers";
import { described } from "./pastPapers";
import { runQueue } from "./queue";
import { NewPaperForm, type RequestResult } from "../papers/NewPaperForm";
import { PaperReadiness } from "../papers/PaperReadiness";
import { PapersSection } from "../papers/PapersSection";
import { MaterialsSection } from "./MaterialsSection";
import { paperReadiness } from "../papers/readiness";
import { ChapterList } from "../papers/ChapterList";
import { PAPER_RULES, describeProfile, isPaperMoving, isStale } from "../papers/describe";
import { ReadyToast, type Settled } from "../papers/ReadyToast";
import { paperLabel } from "../papers/identity";
import {
  docRow,
  materialsNeedAttention,
  materialsSummary,
  paperRow,
  type Bar,
  type RowPill,
} from "./rows";
import { postToS3 } from "./s3Post";
import { DOC_RULES, isTransient } from "./transient";
import { useTransientPolling } from "./useTransientPolling";
import "../styles/library.css";
import type { ApiFailure } from "../api/client";
import { LoadError } from "../states/LoadError";
import { SkeletonRows } from "../states/Skeleton";
import { apiMessage } from "../text/errors";

type Props = {
  api: Api;
  subjectId: string;
  upload?: typeof postToS3;
  sleep?: (ms: number) => Promise<void>;
  onReview?: (bookId: string) => void;
  /** Fix a ready book's chapters (bug fix 1, spec 7). */
  onFix?: (bookId: string) => void;
  onStudy?: (bookId: string) => void;
  onProfile?: (subjectId: string) => void;
  onPaper?: (paperId: string) => void;
  onBack?: () => void;
  /** Something that holds quota moved — a document or paper settled, or a paper was asked for. */
  onSettled?: () => void;
  /** A 402: the workspace may not spend; the Shell sends it to the chooser (spec §9.5). */
  onNotEntitled?: () => void;
};

// Mirrors content.limits.MAX_UPLOAD_BYTES; the presigned policy enforces it server-side.
const MAX = 250 * 1024 * 1024;
const realSleep = (ms: number) => new Promise<void>((r) => setTimeout(r, ms));
// Learning runs after the upload settles: one gated call, a few seconds on the account.
const FORMAT_WATCH = { every: 3000, times: 20 };

export function SubjectScreen({
  api,
  subjectId,
  upload = postToS3,
  sleep = realSleep,
  onReview,
  onFix,
  onStudy,
  onProfile,
  onPaper,
  onBack,
  onSettled,
  onNotEntitled,
}: Props) {
  const [subject, setSubject] = useState<Subject | null>(null);
  const [docs, setDocs] = useState<Doc[] | null>(null);
  const [docsFailed, setDocsFailed] = useState<ApiFailure | null>(null);
  const [attempt, setAttempt] = useState(0);
  // A textbook is one file (one book, one review gate); past papers come as a set.
  const [files, setFiles] = useState<File[]>([]);
  const [kind, setKind] = useState<Doc["kind"]>("textbook");
  // The documents this browser is posting, and the share of each one's bytes sent so far.
  const [uploading, setUploading] = useState<Record<string, number>>({});
  const [failures, setFailures] = useState<(UploadFailure & { file: File })[]>([]);
  const [pastPapers, setPastPapers] = useState<PastPaperListing | null>(null);
  // "Add files" on the folded materials line: open them, then the picker.
  const [pickRequested, setPickRequested] = useState(false);
  const pickRef = useRef<HTMLInputElement>(null);
  useEffect(() => {
    if (!pickRequested) return;
    pickRef.current?.click();
    setPickRequested(false);
  }, [pickRequested]);
  const [error, setError] = useState<string | null>(null);
  const [profile, setProfile] = useState<Profile | "missing" | null>(null);
  const [watching, setWatching] = useState(false);
  const [papers, setPapers] = useState<PaperRow[] | null>(null);
  const [chapters, setChapters] = useState<ChapterWire[]>([]);
  const [quota, setQuota] = useState<Meter | null>(null);
  const [topUpError, setTopUpError] = useState<string | null>(null);
  // Papers asked for, or seen settling, in this visit: highlighted as they land. Each kind
  // has its own animation, so a row made here flashes again when it is written.
  const [flash, setFlash] = useState<Map<string, "new" | "settled">>(() => new Map());
  const [announce, setAnnounce] = useState("");
  const [toast, setToast] = useState<Settled | null>(null);
  // The paper whose name is being edited in its row, if any.
  const [renaming, setRenaming] = useState<string | null>(null);
  // "3 min so far" moves once a minute while a paper is being written.
  const [now, setNow] = useState(() => Date.now());
  const writing = (papers ?? []).some(isPaperMoving);
  useEffect(() => {
    if (!writing) return;
    setNow(Date.now());
    const t = setInterval(() => setNow(Date.now()), 60_000);
    return () => clearInterval(t);
  }, [writing]);
  const [focusPaper, setFocusPaper] = useState<string | null>(null);
  const papersRef = useRef<HTMLElement>(null);

  const loadProfile = useCallback(async () => {
    const r = await api.getProfile(subjectId);
    const next: Profile | "missing" = r.ok ? r.data : "missing";
    setProfile(next);
    return next;
  }, [api, subjectId]);
  // Null until it answers, and stays null from a content without the route: past papers
  // then list as plain documents, as before.
  const loadPastPapers = useCallback(async () => {
    const r = await api.listPastPapers(subjectId);
    if (r.ok) setPastPapers(r.data);
  }, [api, subjectId]);
  const loadQuota = useCallback(async () => {
    const r = await api.billingMe();
    if (r.ok) setQuota(r.data.meters.find((m) => m.name === "papers_generated") ?? null);
  }, [api]);

  useEffect(() => {
    // Merged, not replaced: a row added by an upload that began before the list answered
    // stays, whether or not the list's query saw it.
    void api.listDocuments(subjectId).then((r) => {
      // A failed list is not an empty subject: the error says so, and Try again re-asks.
      setDocsFailed(r.ok ? null : r);
      if (!r.ok) return;
      const listed = r.data.documents;
      setDocs((mine) => [
        ...(mine ?? []).filter((d) => !listed.some((x) => x.id === d.id)),
        ...listed,
      ]);
    });
    void api.listPapers(subjectId).then((r) => setPapers(r.ok ? r.data.papers : []));
    void loadProfile();
    void loadPastPapers();
    void loadQuota();
    void api
      .listSubjects()
      .then((r) => r.ok && setSubject(r.data.subjects.find((x) => x.id === subjectId) ?? null));
  }, [api, subjectId, loadProfile, loadPastPapers, loadQuota, attempt]);

  const readyBook = (docs ?? []).find((d) => d.kind === "textbook" && d.book_status === "ready");
  const readyBookId = readyBook?.book_id ?? null;
  useEffect(() => {
    if (!readyBookId) return setChapters([]);
    void api.listChapters(readyBookId).then((r) => setChapters(r.ok ? r.data.chapters : []));
  }, [api, readyBookId]);

  /** A past paper settled: the format is being learned — keep asking until it appears. */
  async function watchFormat(before: Profile | "missing" | null) {
    setWatching(true);
    try {
      for (let i = 0; i < FORMAT_WATCH.times; i++) {
        await sleep(FORMAT_WATCH.every);
        // The files are read and grouped as they settle, before the format is learned.
        void loadPastPapers();
        const now = await loadProfile();
        if (
          now !== "missing" &&
          (before === "missing" || before === null || now.updated_at !== before.updated_at)
        )
          return;
      }
    } finally {
      setWatching(false);
    }
  }

  // Every poll is rendered, so a row reads pending → uploaded → finding chapters… →
  // chapters need checking → queued → making your flashcards… → ready as it happens.
  const replace = (doc: Doc) => {
    setDocs((d) => (d ?? []).map((x) => (x.id === doc.id ? doc : x)));
    if (!isTransient(doc)) {
      onSettled?.();
      if (doc.kind === "past_paper" && doc.status === "uploaded") void watchFormat(profile);
      else if (doc.kind === "past_paper") void loadPastPapers();
    }
  };
  useTransientPolling(docs, api.getDocument, replace, DOC_RULES, sleep);

  // Only a moving paper is polled, so a settled answer here is one seen moving in this page:
  // a list that loads with ready papers never announces them.
  const replacePaper = (p: PaperRow) => {
    setPapers((x) => (x ?? []).map((y) => (y.id === p.id ? p : y)));
    if (!isPaperMoving(p)) {
      onSettled?.();
      void loadQuota();
      arrived(p);
    }
  };

  function arrived(p: PaperRow) {
    if (p.status !== "ready" && p.status !== "failed") return;
    setFlash((m) => new Map(m).set(p.id, "settled"));
    const label = paperLabel(p);
    setAnnounce(p.status === "ready" ? `${label} is ready.` : `${label} couldn't be written.`);
    const row = papersRef.current?.querySelector<HTMLElement>(`[data-paper="${p.id}"]`);
    if (row && inView(row)) return;
    setToast((t) => {
      const next = t ?? { ready: [], failed: [] };
      const item = { id: p.id, label };
      return p.status === "ready"
        ? { ...next, ready: [...next.ready, item] }
        : { ...next, failed: [...next.failed, item] };
    });
  }
  useTransientPolling(papers, api.getPaper, replacePaper, PAPER_RULES, sleep);

  /** A row for the file, or a failure said where the file is shown. */
  async function createRow(
    f: File,
    asKind: Doc["kind"],
    fail: (f: File, message: string) => void,
    extra: { past_paper_id?: string; role?: PastPaperRole } = {},
  ) {
    if (f.size > MAX) {
      fail(f, "That file is over 250 MB.");
      return null;
    }
    const created = await api.createDocument({
      subject_id: subjectId,
      kind: asKind,
      filename: f.name,
      size_bytes: f.size,
      ...extra,
    });
    if (!created.ok) {
      if (created.status === 402) onNotEntitled?.();
      else fail(f, apiMessage(created.error));
      return null;
    }
    // The row appears now and draws its bytes; the initial list may answer late and already
    // carry this document. The pending row is transient, so polling takes it from here.
    const doc = created.data.document;
    setDocs((d) => [doc, ...(d ?? []).filter((x) => x.id !== doc.id)]);
    return created.data;
  }

  async function send(
    f: File,
    row: { document: Doc; upload: { url: string; fields: Record<string, string> } },
    fail: (f: File, message: string) => void,
  ) {
    const id = row.document.id;
    setUploading((u) => ({ ...u, [id]: 0 }));
    try {
      await upload(row.upload.url, row.upload.fields, f, (fraction) =>
        setUploading((u) => ({ ...u, [id]: fraction })),
      );
    } catch (e) {
      setDocs((d) => (d ?? []).filter((x) => x.id !== id));
      fail(f, e instanceof Error ? e.message : "upload failed");
    } finally {
      setUploading(({ [id]: _, ...rest }) => rest);
    }
  }

  /** One past-paper file's failure, shown on its own line with a Retry. */
  const failPastPaper = (f: File, message: string) =>
    setFailures((x) => [
      ...x.filter((y) => y.file !== f),
      { file: f, name: f.name, message, retry: () => void retryFile(f) },
    ]);

  async function retryFile(f: File) {
    setFailures((x) => x.filter((y) => y.file !== f));
    const row = await createRow(f, "past_paper", failPastPaper);
    if (row) await send(f, row, failPastPaper);
  }

  async function start() {
    const batch = files;
    if (batch.length === 0) return;
    setError(null);
    setFiles([]);
    if (kind === "textbook") {
      const f = batch[0] as File;
      const textbookFail = (_: File, message: string) => setError(message);
      const row = await createRow(f, "textbook", textbookFail);
      if (row) await send(f, row, textbookFail);
      return;
    }
    // Every row first, so the whole set is pending from the first file's event and the
    // format is learned once, after the last (spec 4 §5); then two at a time.
    const rows: [File, NonNullable<Awaited<ReturnType<typeof createRow>>>][] = [];
    for (const f of batch) {
      const row = await createRow(f, "past_paper", failPastPaper);
      if (row) rows.push([f, row]);
    }
    await runQueue(rows, 2, ([f, row]) => send(f, row, failPastPaper));
  }

  async function addFile(pastPaperId: string, role: PastPaperRole, f: File) {
    const row = await createRow(f, "past_paper", failPastPaper, {
      past_paper_id: pastPaperId,
      role,
    });
    if (row) await send(f, row, failPastPaper);
  }

  /** A grouping change answers with the new listing; while the format is a draft (or not yet
   * learned), it is learned again from the new grouping. */
  function regrouped(next: PastPaperListing) {
    setPastPapers(next);
    if (profile === "missing" || (profile !== null && profile.status !== "confirmed"))
      void watchFormat(profile);
  }

  // The rest of a book skipped at review (spec §6.6): what a converted trialist wants first.
  const pending = chapters.filter((c) => c.included && c.ingested_at === null);
  const skipped = chapters.filter((c) => !c.included);
  const pendingPages = pending.reduce((n, c) => n + (c.end_page - c.start_page + 1), 0);

  async function topUp() {
    if (!readyBookId) return;
    setTopUpError(null);
    const r = await api.topUp(readyBookId);
    if (!r.ok) {
      if (r.status === 402) return onNotEntitled?.();
      return setTopUpError(
        r.error === "quota_exceeded"
          ? "That needs more pages than you have left this month."
          : apiMessage(r.error),
      );
    }
    // Confirmed again: the existing progress polling takes it from here.
    setDocs((d) =>
      (d ?? []).map((x) => (x.book_id === readyBookId ? { ...x, book_status: "confirmed" } : x)),
    );
    setChapters([]);
  }

  async function includeAll() {
    if (!readyBookId) return;
    setTopUpError(null);
    const r = await api.putChapters(
      readyBookId,
      chapters.map((c) => ({
        number: c.number,
        title: c.title,
        start_page: c.start_page,
        end_page: c.end_page,
        included: true,
      })),
    );
    if (!r.ok) return setTopUpError(apiMessage(r.error));
    setChapters(r.data.chapters);
  }

  async function requestPaper(body: {
    chapters: number[];
    length: PaperRow["length"];
  }): Promise<RequestResult> {
    const r = await api.requestPaper(subjectId, { book_id: readyBookId ?? "", ...body });
    if (!r.ok) {
      if (r.status === 402) {
        onNotEntitled?.();
        return { ok: false, message: "" };
      }
      return { ok: false, message: apiMessage(r.error) };
    }
    setPapers((p) => [r.data, ...(p ?? [])]);
    setFlash((m) => new Map(m).set(r.data.id, "new"));
    setFocusPaper(r.data.id);
    onSettled?.();
    void loadQuota();
    return { ok: true };
  }

  // The new row is usually out of view of the button: bring it in, and give its title focus
  // so a screen reader reads it.
  useEffect(() => {
    if (!focusPaper) return;
    const row = papersRef.current?.querySelector<HTMLElement>(`[data-paper="${focusPaper}"]`);
    if (!row) return;
    showRow(row);
    row.querySelector<HTMLElement>(".doc__name")?.focus({ preventScroll: true });
    setFocusPaper(null);
  }, [focusPaper, papers]);

  function showPapers() {
    const first = papersRef.current?.querySelector<HTMLElement>("[data-paper]");
    if (first) showRow(first);
    else papersRef.current?.scrollIntoView?.({ block: "start" });
  }

  const learning =
    profile === "missing" &&
    (watching || (docs ?? []).some((d) => d.kind === "past_paper" && d.status === "pending"));
  const known = profile !== null && profile !== "missing" ? profile : null;
  const confirmed = known?.status === "confirmed";
  const made = chapters.filter((c) => c.ingested_at !== null).length;
  const readiness = paperReadiness(
    docs ?? [],
    known,
    learning,
    readyBookId && chapters.length > 0 ? made : null,
  );
  const disabledReason =
    quota && quota.remaining <= 0 ? "You have used this month's papers." : null;
  const byNumber = new Map(chapters.map((c) => [c.number, c]));
  const named = (numbers: number[]) =>
    numbers.flatMap((n) => {
      const c = byNumber.get(n);
      return c ? [c] : [];
    });
  const ingested = chapters.filter((c) => c.ingested_at !== null).length;
  const included = chapters.filter((c) => c.included).length;

  function drop(e: DragEvent) {
    e.preventDefault();
    const pdfs = [...e.dataTransfer.files].filter(
      (f) => f.type === "application/pdf" || /\.pdf$/i.test(f.name),
    );
    setFiles(kind === "past_paper" ? pdfs : pdfs.slice(0, 1));
  }

  // Nothing is decided while the list or the format is still on its way.
  const materialsLoading = docs === null || profile === null;
  const attention =
    !materialsLoading &&
    materialsNeedAttention(docs ?? [], known, learning, pastPapers?.unmatched ?? []);
  const pastPaperCount =
    pastPapers?.papers.length ?? (docs ?? []).filter((d) => d.kind === "past_paper").length;

  // Past papers list as papers once the listing is in hand; until then (or from a content
  // without it) every document is a row, as before.
  const grouped = pastPapers !== null;
  const asRow = (d: Doc) =>
    !grouped || d.kind === "textbook" || d.status === "rejected" || d.status === "withdrawn";
  const listed = new Set((pastPapers?.papers ?? []).flatMap((p) => p.documents.map((d) => d.id)));
  const onTheWay = (docs ?? []).filter(
    (d) =>
      grouped &&
      d.kind === "past_paper" &&
      // Once the listing has it, it is in its paper, whatever this copy of the row still says.
      !listed.has(d.id) &&
      (d.status === "pending" || (d.status === "uploaded" && !described(d))),
  );
  const docItem = (d: Doc) => {
    const r = docRow(d, {
      chapters:
        d.book_id === readyBookId && chapters.length > 0
          ? { ingested, total: included || chapters.length }
          : undefined,
      uploaded: uploading[d.id],
      formatSource: known?.source_document_ids?.includes(d.id) ?? false,
    });
    return (
      <li key={d.id} className="doc">
        <div className="doc__body">
          <span className="doc__name">{d.filename}</span>
          <span className={r.danger ? "doc__meta doc__meta--danger" : "doc__meta"}>{r.meta}</span>
          {r.bar && <RowBar bar={r.bar} />}
        </div>
        <div className="doc__actions">
          {r.pill && <Pill pill={r.pill} />}
          {r.action === "review" && d.book_id && (
            <button className="button" onClick={() => onReview?.(d.book_id ?? "")}>
              Review chapters
            </button>
          )}
          {r.action === "study" && d.book_id && (
            <button className="button button--primary" onClick={() => onStudy?.(d.book_id ?? "")}>
              Study
            </button>
          )}
          {d.kind === "textbook" && d.book_id && onFix && (
            <FixLink bookId={d.book_id} status={d.book_status} onFix={onFix} />
          )}
          {uploading[d.id] === undefined && (
            <button
              className="button button--quiet doc__delete"
              aria-label={`Delete ${d.filename}`}
              onClick={async () => {
                const res = await api.deleteDocument(d.id);
                if (res.ok) {
                  setDocs((x) => (x ?? []).filter((y) => y.id !== d.id));
                  if (d.kind === "past_paper") void loadPastPapers();
                }
              }}
            >
              Delete
            </button>
          )}
        </div>
      </li>
    );
  };

  const paperItem = (p: PaperRow) => {
    const r = paperRow(p, now);
    const lit = flash.get(p.id);
    return (
      <li key={p.id} className={lit ? `doc doc--${lit}` : "doc"} data-paper={p.id}>
        <div className="doc__body">
          {renaming === p.id ? (
            <RenameField
              initial={p.name ?? ""}
              label={`Name for ${paperLabel(p)}`}
              onCancel={() => setRenaming(null)}
              onSave={async (name) => {
                const res = await api.renamePaper(p.id, name);
                if (!res.ok) return apiMessage(res.error);
                setPapers((x) => (x ?? []).map((y) => (y.id === p.id ? res.data : y)));
                setRenaming(null);
                return null;
              }}
            />
          ) : p.status === "ready" ? (
            <button
              type="button"
              className="doc__name doc__name--link"
              onClick={() => onPaper?.(p.id)}
            >
              {r.title}
            </button>
          ) : (
            <span className="doc__name" tabIndex={-1}>
              {r.title}
            </span>
          )}
          <span className={r.danger ? "doc__meta doc__meta--danger" : "doc__meta"}>{r.meta}</span>
          {p.book_id === readyBookId && named(p.chapters).length > 0 && (
            <details className="doc__chapters">
              <summary>Which chapters</summary>
              <ChapterList chapters={named(p.chapters)} />
            </details>
          )}
        </div>
        <div className="doc__actions">
          {r.pill && <Pill pill={r.pill} />}
          {p.status === "ready" && (
            <button className="button button--primary" onClick={() => onPaper?.(p.id)}>
              Open
            </button>
          )}
          {(isStale(p) || p.status === "failed") && (
            <button
              className="button"
              onClick={async () => {
                // A failed paper is queued again; the row starts moving and is polled.
                const res = await api.retryPaper(p.id);
                if (res.ok) setPapers((x) => (x ?? []).map((y) => (y.id === p.id ? res.data : y)));
              }}
            >
              Retry
            </button>
          )}
          {renaming !== p.id && (
            <button
              className="button button--quiet"
              aria-label={`Rename ${paperLabel(p)}`}
              onClick={() => setRenaming(p.id)}
            >
              Rename
            </button>
          )}
          <button
            className="button button--quiet doc__delete"
            aria-label="Delete paper"
            onClick={async () => {
              const res = await api.deletePaper(p.id);
              if (res.ok) setPapers((x) => (x ?? []).filter((y) => y.id !== p.id));
            }}
          >
            Delete
          </button>
        </div>
      </li>
    );
  };

  return (
    <main className="library subject">
      <button className="subject__back" onClick={() => onBack?.()}>
        ‹ Subjects
      </button>
      <h1 className="subject__title">
        {subject?.name ?? "Subject"}
        {subject?.qualification && <span className="subject__qual">{subject.qualification}</span>}
      </h1>
      <div className="subject__grid">
        <div className="subject__main">
          <PapersSection papers={papers} renderRow={paperItem} sectionRef={papersRef} />

          <MaterialsSection
            loading={materialsLoading}
            attention={attention}
            summary={materialsSummary(docs ?? [], pastPaperCount, known)}
            onAddFiles={() => setPickRequested(true)}
            primary={
              readyBook?.book_id ? (
                <button
                  type="button"
                  className="button button--primary"
                  onClick={() => onStudy?.(readyBook.book_id ?? "")}
                >
                  Study
                </button>
              ) : undefined
            }
          >
            {docsFailed && !docs?.length ? (
              <LoadError failure={docsFailed} onRetry={() => setAttempt((n) => n + 1)} />
            ) : docs === null ? (
              <SkeletonRows rows={2} />
            ) : docs.length === 0 ? (
              <div className="subject__empty">
                <h3 className="subject__empty-title">No documents yet</h3>
                <p className="panel__text">
                  Upload the textbook first. A past paper can come later; it’s only needed for
                  practice papers.
                </p>
              </div>
            ) : (
              <>
                {docs.some(asRow) && (
                  <ul className="doc-list">{docs.filter(asRow).map(docItem)}</ul>
                )}
                {pastPapers && (
                  <PastPapers
                    listing={pastPapers}
                    uploading={onTheWay.map(docItem)}
                    failures={failures}
                    onMove={async (id, paperId) => {
                      const r = await api.placeDocument(id, { past_paper_id: paperId });
                      if (r.ok) regrouped(r.data);
                    }}
                    onRole={async (id, role) => {
                      const r = await api.placeDocument(id, { role });
                      if (r.ok) regrouped(r.data);
                    }}
                    onRename={async (id, name) => {
                      const r = await api.renamePastPaper(id, name);
                      if (r.ok) setPastPapers(r.data);
                    }}
                    onDeletePaper={async (id) => {
                      const gone = new Set(
                        pastPapers.papers.find((p) => p.id === id)?.documents.map((d) => d.id) ??
                          [],
                      );
                      const r = await api.deletePastPaper(id);
                      if (!r.ok) return;
                      setDocs((x) => (x ?? []).filter((y) => !gone.has(y.id)));
                      void loadPastPapers();
                    }}
                    onDeleteFile={async (id) => {
                      const r = await api.deleteDocument(id);
                      if (!r.ok) return;
                      setDocs((x) => (x ?? []).filter((y) => y.id !== id));
                      void loadPastPapers();
                    }}
                    onAddFile={addFile}
                  />
                )}
              </>
            )}
            <div
              className="drop"
              onDragOver={(e) => e.preventDefault()}
              onDrop={drop}
              aria-label="Add a PDF"
              role="group"
            >
              <div className="drop__text">
                <h3 className="drop__title">Add a PDF</h3>
                <p className="drop__note">
                  Up to 250 MB. Used only to make your material; it is never shown or offered for
                  download.
                </p>
              </div>
              <div className="drop__controls">
                <div className="segmented" role="radiogroup" aria-label="Kind">
                  {(
                    [
                      ["textbook", "Textbook"],
                      ["past_paper", "Past paper"],
                    ] as const
                  ).map(([value, label]) => (
                    <label
                      key={value}
                      className={`segmented__option${kind === value ? " segmented__option--on" : ""}`}
                    >
                      <input
                        type="radio"
                        name="kind"
                        value={value}
                        checked={kind === value}
                        onChange={() => setKind(value)}
                      />
                      {label}
                    </label>
                  ))}
                </div>
                <label className="button drop__pick">
                  Choose file
                  <input
                    type="file"
                    accept="application/pdf"
                    aria-label="Choose a PDF"
                    ref={pickRef}
                    multiple={kind === "past_paper"}
                    onChange={(e) => setFiles([...(e.target.files ?? [])])}
                  />
                </label>
              </div>
              {files.length > 0 && (
                <p className="drop__chosen">
                  <span className="drop__file">
                    {files.length === 1 ? files[0]!.name : `${files.length} files`}
                  </span>
                  <span className="muted num">
                    {" "}
                    · {(files.reduce((n, f) => n + f.size, 0) / 1_000_000).toFixed(1)} MB
                  </span>
                  <button
                    className="button button--primary"
                    disabled={kind === "textbook" && Object.keys(uploading).length > 0}
                    onClick={() => void start()}
                  >
                    Upload
                  </button>
                </p>
              )}
              {error && (
                <p role="alert" className="field-error">
                  {error}
                </p>
              )}
            </div>
          </MaterialsSection>
        </div>

        <aside className="subject__side">
          {docs === null || profile === null ? (
            <section className="panel new-paper" aria-label="New paper">
              <h2>New paper</h2>
              <SkeletonRows rows={2} />
            </section>
          ) : readiness.ready ? (
            <NewPaperForm
              // The chapters arrive after the first render; a new list remounts the form ticked.
              key={chapters.map((c) => `${c.number}:${c.ingested_at !== null}`).join(",")}
              chapters={chapters.map((c) => ({
                number: c.number,
                title: c.title,
                made: c.ingested_at !== null,
              }))}
              fullMarks={confirmed && known ? known.total_marks : 0}
              fullMinutes={confirmed && known ? known.duration_minutes : 0}
              remaining={quota?.remaining ?? 0}
              limit={quota?.limit ?? 10}
              disabledReason={disabledReason}
              onRequest={requestPaper}
              onShowPapers={showPapers}
            />
          ) : (
            <PaperReadiness
              readiness={readiness}
              onCheckChapters={(bookId) => onReview?.(bookId)}
              onCheckFormat={() => onProfile?.(subjectId)}
            />
          )}

          <section className="panel" aria-label="Exam format">
            <div className="panel__head">
              <h2>Exam format</h2>
              {known && known.status !== "failed" && (
                <button className="subject__link" onClick={() => onProfile?.(subjectId)}>
                  {known.status === "draft" ? "Check the format" : "View the format"}
                </button>
              )}
            </div>
            {profile === null ? (
              <span
                className="skeleton"
                style={{ width: "70%" }}
                role="status"
                aria-label="Loading"
              />
            ) : (
              <p className="panel__text">
                {describeProfile(profile === "missing" ? null : profile, learning)}
              </p>
            )}
          </section>

          {readyBook && (pending.length > 0 || skipped.length > 0) && (
            <section className="panel" aria-label="Chapters without flashcards yet">
              <h2>The rest of the book</h2>
              <p className="panel__text">
                {pending.length > 0
                  ? `${pending.length} chapter${pending.length === 1 ? "" : "s"} without flashcards yet`
                  : `${skipped.length} chapter${skipped.length === 1 ? "" : "s"} left out at review — no flashcards or papers from ${skipped.length === 1 ? "it" : "them"} yet`}
              </p>
              <ChapterList chapters={pending.length > 0 ? pending : skipped} shown={3} />
              {onFix && readyBookId && (
                <button type="button" className="subject__link" onClick={() => onFix(readyBookId)}>
                  Fix chapters
                </button>
              )}
              {pending.length > 0 ? (
                <button className="button" onClick={() => void topUp()}>
                  Add them · {pendingPages} page{pendingPages === 1 ? "" : "s"}
                </button>
              ) : (
                <button className="button" onClick={() => void includeAll()}>
                  Include them
                </button>
              )}
              {topUpError && (
                <p role="alert" className="field-error">
                  {topUpError}
                </p>
              )}
            </section>
          )}
        </aside>
      </div>
      <p className="sr-only" aria-live="polite">
        {announce}
      </p>
      {toast && (
        <ReadyToast
          settled={toast}
          onOpen={(id) => onPaper?.(id)}
          onShowPapers={showPapers}
          onDismiss={() => setToast(null)}
        />
      )}
      {subject && <DeleteSubject api={api} subject={subject} onDeleted={() => onBack?.()} />}
    </main>
  );
}

/** At the foot, asked twice: a subject takes its books, decks, format and papers with it. */
function DeleteSubject({
  api,
  subject,
  onDeleted,
}: {
  api: Api;
  subject: Subject;
  onDeleted: () => void;
}) {
  const [asking, setAsking] = useState(false);
  const [busy, setBusy] = useState(false);
  const [failed, setFailed] = useState(false);
  async function remove() {
    setBusy(true);
    setFailed(false);
    const r = await api.deleteSubject(subject.id);
    setBusy(false);
    if (r.ok) return onDeleted();
    setFailed(true);
  }
  return (
    <section className="subject__delete" aria-label="Delete this subject">
      {!asking ? (
        <button className="button button--quiet" onClick={() => setAsking(true)}>
          Delete subject
        </button>
      ) : (
        <>
          <p className="subject__delete-text">
            Delete {subject.name} and everything in it? Its documents, flashcards, exam format and
            papers go too, and cannot be brought back.
          </p>
          <div className="subject__delete-actions">
            <button className="button button--danger" disabled={busy} onClick={() => void remove()}>
              Delete {subject.name}
            </button>
            <button className="button" disabled={busy} onClick={() => setAsking(false)}>
              Keep it
            </button>
          </div>
          {failed && (
            <p role="alert" className="field-error">
              That didn't delete. Try again.
            </p>
          )}
        </>
      )}
    </section>
  );
}

/** "Fix chapters" on a book's row: once its flashcards are made; while they are being made,
 * shown and disabled with why. */
function FixLink({
  bookId,
  status,
  onFix,
}: {
  bookId: string;
  status: Doc["book_status"];
  onFix: (bookId: string) => void;
}) {
  if (status === "ready")
    return (
      <button type="button" className="button button--quiet" onClick={() => onFix(bookId)}>
        Fix chapters
      </button>
    );
  if (status === "confirmed" || status === "ingesting")
    return (
      <button
        type="button"
        className="button button--quiet"
        disabled
        title="Available when your flashcards are ready"
        aria-label="Fix chapters — available when your flashcards are ready"
      >
        Fix chapters
      </button>
    );
  return null;
}

/** A paper's name, edited in its row: Enter saves, Escape cancels; empty clears it. */
function RenameField({
  initial,
  label,
  onSave,
  onCancel,
}: {
  initial: string;
  label: string;
  onSave: (name: string | null) => Promise<string | null>;
  onCancel: () => void;
}) {
  const [value, setValue] = useState(initial);
  const [failure, setFailure] = useState<string | null>(null);
  return (
    <span className="doc__rename">
      <input
        className="input"
        aria-label={label}
        value={value}
        maxLength={60}
        autoFocus
        placeholder="A name, like Mock before half term"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={async (e) => {
          if (e.key === "Escape") return onCancel();
          if (e.key !== "Enter") return;
          e.preventDefault();
          setFailure(await onSave(value.trim() || null));
        }}
      />
      {failure && (
        <span role="alert" className="field-error">
          {failure}
        </span>
      )}
    </span>
  );
}

/** Some of the row is on screen. */
function inView(row: HTMLElement): boolean {
  const r = row.getBoundingClientRect();
  return r.height > 0 && r.bottom > 0 && r.top < window.innerHeight;
}

/** Into view without a jump when the student has asked for less motion. */
function showRow(row: HTMLElement) {
  const still = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches ?? false;
  row.scrollIntoView?.({ block: "nearest", behavior: still ? "auto" : "smooth" });
}

function Pill({ pill }: { pill: RowPill }) {
  return <span className={`pill pill--${pill.kind}`}>{pill.label}</span>;
}

function RowBar({ bar }: { bar: Bar }) {
  return (
    <span className={`doc__bar doc__bar--${bar.tone}`} aria-hidden="true">
      <span style={{ width: `${Math.round(bar.fraction * 100)}%` }} />
    </span>
  );
}
